/**
 * cascadingSampler — 4-stage elevation sampling cascade (EAS-3).
 *
 * Pack: `docs/prompt-packs/Elevation_Averaging_Sampling_Prompt.md`.
 *
 * Live-prod 2026-05-18 user feedback on Redhead — PRE shipped the
 * honest "Provider returned no elevation data for this polygon" chip,
 * but the underlying fetcher only sampled the polygon vertices once
 * and gave up. Small polygons + coverage-edge polygons fail this
 * test more often than they should. The cascade gives the fetcher
 * four chances before declaring empty:
 *
 *   Stage 1: Polygon vertices (current behaviour — preserves
 *            back-compat with every site that already worked).
 *   Stage 2: In-polygon n×n grid (default 5×5 → up to 20 points,
 *            capped at MAX_SAMPLES_PER_STAGE).
 *   Stage 3: Buffered-bbox n×n grid (100 m buffer, no in-polygon
 *            filter). Captures sites where Google has data NEAR the
 *            polygon but not at the vertices themselves.
 *   Stage 4: USGS EPQS centroid (free, US-CONUS). Last-ditch when
 *            Google has nothing.
 *
 * Each stage short-circuits the cascade as soon as it returns ≥3
 * samples — fewer than 3 is below the EL3 "refuse to estimate from
 * one sample" threshold, so we keep trying. When all four stages
 * fail the result is `attemptResult: 'empty'` with a stage-by-stage
 * audit trail; the producer surface stamps PRE chip text from it.
 *
 * Cost envelope (per HARD RULE #9):
 *   - Each Google stage = 1 request, regardless of point count
 *     (Google bills per request). At ~$0.005/req × max 3 Google
 *     stages = ~$0.015/site worst case.
 *   - USGS EPQS is free.
 *   - Total cap per cascade: ≤4 upstream calls. Stamped in the
 *     result as `pointsRequested` per stage.
 *
 * Numerical fidelity (per HARD RULE #10):
 *   - When a stage succeeds, the cascade returns the raw per-point
 *     sample list. The caller (siteEvidence producer) computes
 *     min/max/avg/stdev from `samples.map(s => s.elevationMeters)`.
 *   - Population stdev (divisor = N) matches the existing
 *     fetchPolygonElevations convention.
 *
 * Pure of React / Zustand — fully unit-testable with mocked fetch
 * (HARD RULE #8).
 */

import {
  polygonInscribedGrid,
  bufferedBboxGrid,
  polygonCentroid,
  polygonAreaM2,
  MIN_POLYGON_AREA_M2,
  MAX_SAMPLES_PER_STAGE,
  ACRES_TO_M2,
  SMALL_POLYGON_ACRES,
  DENSE_GRID_MAX_SAMPLES,
  DENSE_GRID_N,
  type LatLng,
} from '../../../lib/geometry/sampleGrid';
import { rethrowIfAborted, throwIfAborted } from '../../../lib/abortSignal';
import {
  fetchGoogleElevationForPoints,
  type GoogleElevationSample,
} from './googleElevationPoints';
import { fetchUsgsEpqs } from './usgsEpqsFetch';
import { refdataElevationPoints, tryRefdata, isPrivateRefdataAcceptanceDataset } from '../../refdata/refdataClient';

/** Stage identifier — used as the chip-vocabulary qualifier in PRE.
 *  RD-1 (C6) adds Stage 0: the self-hosted USGS 3DEP snapshot in GCS,
 *  sampled ahead of every paid Google stage. */
export type ElevationSamplingStage =
  | 'refdata-3dep-grid'
  | 'vertex'
  | 'in-polygon-grid'
  | 'buffered-bbox-grid'
  | 'usgs-epqs';

/** Per-stage attempt record. Stored on the SED for the appendix +
 *  tooltip ("Tried 4 strategies: vertex 0/4 returned · …"). */
export interface ElevationSamplingStageRecord {
  stage: ElevationSamplingStage;
  pointsRequested: number;
  pointsReturned: number;
  durationMs: number;
  /** Optional short error string for diagnostic stamping (no PII). */
  error?: string;
}

/** Final cascade result. `samples` empty + `attemptResult === 'empty'`
 *  ⇒ all four stages tried and none returned ≥1 sample. */
export interface ElevationSamplingResult {
  sourceQualification?: import('../../../types/elevationSourceQualification').ElevationSourceQualification;
  samples: GoogleElevationSample[];
  stages: ElevationSamplingStageRecord[];
  /** Stage-qualified provenance string, surfaced in PRE chip. */
  source:
    | 'refdata-3dep:grid'
    | 'google-elevation:vertex'
    | 'google-elevation:in-polygon-grid'
    | 'google-elevation:buffered-bbox-grid'
    | 'usgs-epqs:centroid'
    | 'google-elevation:none';
  attemptResult: 'success' | 'empty';
  /** Human-readable reason for the empty branch — surfaced in the
   *  PRE empty chip's tooltip. */
  emptyReason?: string;
}

/**
 * Minimum samples required for a Google stage (1-3) to count as
 * "succeeded" and short-circuit the cascade. Below this threshold
 * we keep trying — a single point on land that happens to fall in a
 * drainage ditch is misleading (same EL3 rationale).
 *
 * Stage 4 (USGS centroid) intentionally returns only 1 sample by
 * design — the threshold for that stage is 1, not 3.
 */
const MIN_SAMPLES_GOOGLE_STAGE = 3;

/** Polygon shape — matches `SiteEntity.siteBoundary`. */
export type Polygon = readonly LatLng[];

interface CascadeOptions {
  /** Attempt-owned cancellation signal. Superseded Studio analyses abort
   * every remaining cascade stage and may not degrade into an empty result. */
  signal?: AbortSignal;
  /** Override the Google Elevation fetcher (tests inject a mock). */
  fetchGoogle?: typeof fetchGoogleElevationForPoints;
  /** Override the USGS EPQS fetcher (tests inject a mock). */
  fetchUsgs?: typeof fetchUsgsEpqs;
  /** Override the refdata 3DEP sampler (tests inject a mock). */
  fetchRefdata?: typeof refdataElevationPoints;
  /** Grid resolution (per axis) for Stages 2 + 3. Default 5 ⇒
   *  up to 25 candidates, capped at MAX_SAMPLES_PER_STAGE. */
  gridN?: number;
  /** Buffer distance for Stage 3, in metres. Default 100 m. */
  bufferMeters?: number;
}

/**
 * Run one Google stage. Always stamps exactly ONE stage record on
 * `stages` (even on error / empty / skipped). Returns the sample
 * list — caller decides whether to short-circuit.
 */
async function runGoogleStage(
  stage: 'vertex' | 'in-polygon-grid' | 'buffered-bbox-grid',
  points: readonly LatLng[],
  fetchGoogle: typeof fetchGoogleElevationForPoints,
  stages: ElevationSamplingStageRecord[],
  signal?: AbortSignal,
  extraNote?: string,
): Promise<GoogleElevationSample[]> {
  throwIfAborted(signal);
  const t0 = Date.now();
  let samples: GoogleElevationSample[] = [];
  let errMsg: string | undefined;
  if (points.length === 0) {
    // Nothing to query — stamp the skip + return.
    stages.push({
      stage,
      pointsRequested: 0,
      pointsReturned: 0,
      durationMs: Date.now() - t0,
      ...(extraNote ? { error: extraNote } : {}),
    });
    return samples;
  }
  try {
    samples = (await fetchGoogle(points, undefined, signal)) ?? [];
    throwIfAborted(signal);
  } catch (e) {
    rethrowIfAborted(signal, e);
    errMsg = (e as Error)?.message ?? 'unknown';
  }
  stages.push({
    stage,
    pointsRequested: points.length,
    pointsReturned: samples.length,
    durationMs: Date.now() - t0,
    ...(errMsg ? { error: errMsg } : extraNote ? { error: extraNote } : {}),
  });
  return samples;
}

/**
 * Run the cascade for the given polygon. Returns a fully-stamped
 * result whose `stages[]` is the audit trail of every stage actually
 * executed (the cascade stops as soon as one succeeds; downstream
 * stages do not appear).
 *
 * @param ring     Polygon vertices (`{lat,lng}[]`). Fewer than 3
 *                 short-circuits to `attemptResult: 'empty'` with no
 *                 upstream calls — there's no polygon to sample.
 * @param options  Optional fetch overrides + grid / buffer overrides.
 */
export async function sampleElevationCascading(
  ring: Polygon,
  options: CascadeOptions = {},
): Promise<ElevationSamplingResult> {
  const fetchGoogle = options.fetchGoogle ?? fetchGoogleElevationForPoints;
  const fetchUsgs_ = options.fetchUsgs ?? fetchUsgsEpqs;
  const gridN = options.gridN ?? 5;
  const bufferMeters = options.bufferMeters ?? 100;
  const signal = options.signal;

  const stages: ElevationSamplingStageRecord[] = [];
  throwIfAborted(signal);

  if (!Array.isArray(ring) || ring.length < 3) {
    return {
      samples: [],
      stages,
      source: 'google-elevation:none',
      attemptResult: 'empty',
      emptyReason:
        'No polygon to sample (fewer than 3 vertices). Draw a site boundary to retry.',
    };
  }

  // Compute the polygon's area once — drives the "polygon too small"
  // diagnostic stamped into the audit trail + empty reason AND the
  // XO-3 dense-grid escalation below.
  const area = polygonAreaM2(ring);

  // ── XO-3 EAS-FOLLOWUP-DENSE-GRID ─────────────────────────────────
  //
  // For small polygons (< SMALL_POLYGON_ACRES) escalate Stage 2 + 3
  // to a 6×6 grid (up to DENSE_GRID_MAX_SAMPLES = 36 points) instead
  // of the default 5×5 (capped at MAX_SAMPLES_PER_STAGE = 20). Better
  // statistical accuracy on small parcels where the default cell
  // spacing collides with Google Elevation's tile resolution.
  //
  // Cost envelope (HARD RULE #9): Google bills per *request*, not per
  // point, and the helper still issues exactly ONE batched request per
  // stage — the 36-vs-20 difference only changes the URL payload length
  // (well below the proxy's limit). So the escalation does NOT cost
  // extra; it just spends a bit more bandwidth for the same query.
  //
  // Above the threshold we keep the default 5×5/20 envelope so large
  // polygons don't densify pointlessly (the 5×5 grid already crosses
  // ≥1 tile per row at 10+ acres).
  //
  // Caller can still override via `options.gridN`; only the default
  // path picks up the escalation. The explicit override case keeps
  // existing tests + admin-tuning surfaces unchanged.
  const smallPolygonM2 = SMALL_POLYGON_ACRES * ACRES_TO_M2;
  const denseEscalation = options.gridN === undefined && area > 0 && area < smallPolygonM2;
  const effectiveGridN = denseEscalation ? DENSE_GRID_N : gridN;
  const effectiveGridCap = denseEscalation ? DENSE_GRID_MAX_SAMPLES : MAX_SAMPLES_PER_STAGE;

  // Point sets shared by Stage 0 and the Google stages. Stage 1 vertex
  // semantics: cap to MAX_SAMPLES_PER_STAGE (≤20 vertices + 1 centroid
  // would exceed; truncate vertices first so the centroid always
  // survives) — preserved exactly from the pre-RD-1 cascade.
  const centroidStage1 = polygonCentroid(ring);
  const cappedVerts = ring.slice(
    0,
    Math.max(0, MAX_SAMPLES_PER_STAGE - (centroidStage1 ? 1 : 0)),
  );
  const vertexPoints: LatLng[] = centroidStage1
    ? [...cappedVerts, centroidStage1]
    : [...cappedVerts];
  const gridPoints = polygonInscribedGrid(ring, effectiveGridN, effectiveGridCap);

  // ── Stage 0 — Refdata 3DEP grid (RD-1/C6) ────────────────────────
  //
  // The self-hosted USGS 1-arc-second snapshot in GCS samples the
  // vertex+centroid set AND the in-polygon grid in ONE free local
  // call, ahead of every paid Google stage. A miss (dataset unseeded,
  // engine disabled, GCS hiccup) falls through to the untouched
  // Google→EPQS cascade; ≥3 valid samples short-circuits exactly like
  // a Google stage. Per-point nulls (nodata / outside coverage) are
  // dropped, not zero-filled.
  const fetchRefdata = options.fetchRefdata ?? refdataElevationPoints;
  const stage0Points: LatLng[] = [...vertexPoints, ...gridPoints];
  if (stage0Points.length > 0) {
    const t0 = Date.now();
    const refdataResult = await tryRefdata('usgs-3dep', () => fetchRefdata(stage0Points));
    throwIfAborted(signal);
    const stage0Samples: GoogleElevationSample[] = [];
    if (refdataResult && Array.isArray(refdataResult.elevations)) {
      refdataResult.elevations.forEach((elevation, i) => {
        if (elevation != null && Number.isFinite(elevation)) {
          stage0Samples.push({
            lat: stage0Points[i].lat,
            lng: stage0Points[i].lng,
            elevationMeters: elevation,
          });
        }
      });
    }
    stages.push({
      stage: 'refdata-3dep-grid',
      pointsRequested: stage0Points.length,
      pointsReturned: stage0Samples.length,
      durationMs: Date.now() - t0,
      ...(refdataResult ? {} : { error: 'refdata-3dep unavailable' }),
    });
    if (stage0Samples.length >= MIN_SAMPLES_GOOGLE_STAGE) {
      return {
        samples: stage0Samples,
        stages,
        source: 'refdata-3dep:grid',
        ...(refdataResult?.sourceQualification ? { sourceQualification: refdataResult.sourceQualification } : {}),
        attemptResult: 'success',
      };
    }
    if (isPrivateRefdataAcceptanceDataset('usgs-3dep')) return {
      samples: [], stages, source: 'refdata-3dep:grid', attemptResult: 'empty',
      emptyReason: `The frozen elevation snapshot returned ${stage0Samples.length} usable samples; at least ${MIN_SAMPLES_GOOGLE_STAGE} are needed. Live supplementation is withheld.`,
    };
  }

  // ── Stage 1 — Polygon vertices (+ arithmetic centroid) ──────────
  //
  // Back-compat invariant (EAS pack MUST-READ §3): existing polygons
  // that returned data via vertex-only sampling continue to produce
  // identical Min/Max/Avg/Stdev. The legacy `fetchPolygonElevations`
  // appended the polygon centroid as an N+1th sample to keep the
  // population-stdev divisor consistent with what every shipped SED
  // already records, so we preserve that here.
  const stage1Samples = await runGoogleStage('vertex', vertexPoints, fetchGoogle, stages, signal);
  if (stage1Samples.length >= MIN_SAMPLES_GOOGLE_STAGE) {
    return {
      samples: stage1Samples,
      stages,
      source: 'google-elevation:vertex',
      attemptResult: 'success',
    };
  }

  // ── Stage 2 — In-polygon grid ────────────────────────────────────
  // XO-3: small polygons get a 6×6/36-cap densification; large
  // polygons keep the default 5×5/20-cap envelope. (gridPoints hoisted
  // above Stage 0, which samples the same set.)
  const stage2Extra =
    gridPoints.length === 0 && area < MIN_POLYGON_AREA_M2
      ? `polygon-area-below-${MIN_POLYGON_AREA_M2}m2`
      : undefined;
  const stage2Samples = await runGoogleStage(
    'in-polygon-grid',
    gridPoints,
    fetchGoogle,
    stages,
    signal,
    stage2Extra,
  );
  if (stage2Samples.length >= MIN_SAMPLES_GOOGLE_STAGE) {
    return {
      samples: stage2Samples,
      stages,
      source: 'google-elevation:in-polygon-grid',
      attemptResult: 'success',
    };
  }

  // ── Stage 3 — Buffered-bbox grid ─────────────────────────────────
  // XO-3: same densification rule as Stage 2 — the buffered bbox is
  // also a grid sampler, so densifying it helps small polygons too.
  const bufferedPoints = bufferedBboxGrid(
    ring,
    bufferMeters,
    effectiveGridN,
    effectiveGridCap,
  );
  const stage3Samples = await runGoogleStage(
    'buffered-bbox-grid',
    bufferedPoints,
    fetchGoogle,
    stages,
    signal,
  );
  if (stage3Samples.length >= MIN_SAMPLES_GOOGLE_STAGE) {
    return {
      samples: stage3Samples,
      stages,
      source: 'google-elevation:buffered-bbox-grid',
      attemptResult: 'success',
    };
  }

  // ── Stage 4 — USGS EPQS centroid ─────────────────────────────────
  const centroid = polygonCentroid(ring);
  const t4 = Date.now();
  let usgsSample: Awaited<ReturnType<typeof fetchUsgsEpqs>> = null;
  let usgsErr: string | undefined;
  if (centroid) {
    try {
      usgsSample = await fetchUsgs_(centroid, undefined, undefined, signal);
      throwIfAborted(signal);
    } catch (e) {
      rethrowIfAborted(signal, e);
      usgsErr = (e as Error)?.message ?? 'unknown';
    }
  }
  stages.push({
    stage: 'usgs-epqs',
    pointsRequested: centroid ? 1 : 0,
    pointsReturned: usgsSample ? 1 : 0,
    durationMs: Date.now() - t4,
    ...(usgsErr ? { error: usgsErr } : {}),
  });
  if (usgsSample) {
    // Stage 4 returns 1 sample by design — accept it. (The EL3
    // ≥3-samples threshold doesn't apply to a single-point service.)
    return {
      samples: [usgsSample],
      stages,
      source: 'usgs-epqs:centroid',
      attemptResult: 'success',
    };
  }

  // ── All four stages failed ───────────────────────────────────────
  const stageList = stages.map(s => s.stage).join(', ');
  const totalReturned = stages.reduce((s, st) => s + st.pointsReturned, 0);
  return {
    samples: [],
    stages,
    source: 'google-elevation:none',
    attemptResult: 'empty',
    emptyReason:
      totalReturned === 0
        ? `Provider returned no data after [${stageList}] — polygon may be outside coverage` +
          (area < MIN_POLYGON_AREA_M2 ? ` or too small (area ≈ ${Math.round(area)} m²)` : '') +
          '.'
        : `Stages [${stageList}] each returned fewer than ${MIN_SAMPLES_GOOGLE_STAGE} samples — declining to estimate from outliers.`,
  };
}
